import { useEffect, useId, useRef, useState } from 'react'
import { portalLinks } from './portal-links'
import './web-test-faq.css'
import { trackSupport } from '../analytics/track'

export const HOMEPAGE_FAQ_ITEMS = [
  {
    q: 'What is Perfect Predict?',
    a: 'A structured prediction trading experience with predefined strategy parameters and MetaTrader 5 execution.',
  },
  {
    q: 'Which markets are currently available?',
    a: 'Gold and Silver. Additional markets may be introduced over time.',
  },
  {
    q: 'Does Perfect Predict decide whether I BUY or SELL?',
    a: 'No. The trader always chooses direction.',
  },
  {
    q: 'Do I need to fund a Demo account?',
    a: 'No. Demo accounts include a virtual opening balance.',
  },
  {
    q: 'How do I fund my Live Trading Account?',
    a: 'Through the Client Portal using the supported funding method (Uniwire).',
  },
  {
    q: 'Which platform is used for execution?',
    a: 'MetaTrader 5 infrastructure.',
  },
  {
    q: 'Where do I manage my account?',
    a: 'In the Perfect Predict Client Portal.',
  },
] as const

const QUESTIONS = HOMEPAGE_FAQ_ITEMS

export function WebTestFaq() {
  const rootRef = useRef<HTMLElement>(null)
  const baseId = useId()
  const [visible, setVisible] = useState(false)
  const [openIndex, setOpenIndex] = useState(0)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.12 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section
      ref={rootRef}
      id="faq"
      className={`wq-section${visible ? ' is-visible' : ''}`}
      aria-labelledby="wq-title"
    >
      <div id="common-questions" />
      <div className="wq-inner">
        <div className="wq-layout">
          <header className="wq-intro pp-enter" style={{ ['--pp-i' as string]: 0 }}>
            <p className="wq-eyebrow">FAQ</p>
            <h2 id="wq-title" className="wq-title">
              Common Questions
            </h2>
            <p className="wq-subtitle">Short answers before you open a Live Trading Account.</p>
          </header>

          <div className="wq-list pp-enter" style={{ ['--pp-i' as string]: 1 }}>
            {QUESTIONS.map((item, index) => {
              const open = openIndex === index
              const panelId = `${baseId}-panel-${index}`
              const buttonId = `${baseId}-button-${index}`
              return (
                <article key={item.q} className={`wq-item${open ? ' is-open' : ''}`}>
                  <h3>
                    <button
                      id={buttonId}
                      type="button"
                      className="wq-item__trigger"
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(open ? -1 : index)}
                    >
                      <span>{item.q}</span>
                      <span className="wq-item__icon" aria-hidden="true">
                        {open ? '−' : '+'}
                      </span>
                    </button>
                  </h3>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="wq-item__panel"
                    hidden={!open}
                  >
                    <p>{item.a}</p>
                  </div>
                </article>
              )
            })}
          </div>
        </div>

        <div className="wq-footer pp-enter" style={{ ['--pp-i' as string]: 2 }}>
          <p className="wq-footer__text">Still have questions? Our support team can help.</p>
          <a
            className="wt-btn wt-btn--secondary wt-btn--hero"
            href={portalLinks.support}
            onClick={() => trackSupport('footer')}
          >
            Contact Support
          </a>
        </div>
      </div>
    </section>
  )
}
